import { CommonModule } from '@angular/common'
import { AfterViewInit, Component, DestroyRef, ElementRef, HostListener, inject, NgZone, OnInit, ViewChild } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { ActivatedRoute, Router } from '@angular/router'
import { DropdownComponent } from '@app/v2/shared/components/dropdown/dropdown.component'
import { DropdownOption } from '@app/v2/shared/components/dropdown/types/dropdown-option.type'
import { AuthService } from '../../../auth/services/auth.service'
import { BlacklistComponent } from '../blacklist/blacklist.component'
import { EmailComposerComponent } from '../email-composer/email-composer.component'
import { EventsCalendarComponent } from '../events-calendar/events-calendar.component'
import { FeatureFlagsComponent } from '../feature-flags/feature-flags.component'
import { MigrateEventsComponent } from '../migrate-events/migrate-events.component'
import { StatisticsComponent } from '../statistics/statistics.component'
import { WorkspaceSwitcherComponent } from '../workspace-switcher/workspace-switcher.component'
import { EventToolsHubComponent } from './components/event-tools-hub/event-tools-hub.component'
import { FileUploaderComponent } from './components/file-uploader/file-uploader.component'
import { UrlShortenerComponent } from './components/url-shortener/url-shortener.component'

@Component({
  selector: 'ngx-admin-tools',
  templateUrl: './tools.component.html',
  styleUrls: ['./tools.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    WorkspaceSwitcherComponent,
    StatisticsComponent,
    UrlShortenerComponent,
    FileUploaderComponent,
    EmailComposerComponent,
    DropdownComponent,
    MigrateEventsComponent,
    EventsCalendarComponent,
    FeatureFlagsComponent,
    EventToolsHubComponent,
    BlacklistComponent
  ]
})
export class ToolsComponent implements OnInit, AfterViewInit {
  activeView: string = 'url-shortener' // Default to URL shortener instead of statistics
  selectedStatisticTab: number = 0

  private readonly _legacyEventToolTabs = new Set(['badge-editor', 'group-forms', 'confirmation-setup', 'options', 'settings'])

  public statisticsOptions: DropdownOption[] = [
    {
      key: 0,
      value: 'Events Monthly',
      prefixIcon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M19,3H5C3.9,3 3,3.9 3,5V19C3,20.1 3.9,21 5,21H19C20.1,21 21,20.1 21,19V5C21,3.9 20.1,3 19,3M19,19H5V5H19V19Z" /></svg>`
    },
    {
      key: 1,
      value: 'Workspaces',
      prefixIcon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M12,5.5A3.5,3.5 0 0,1 15.5,9A3.5,3.5 0 0,1 12,12.5A3.5,3.5 0 0,1 8.5,9A3.5,3.5 0 0,1 12,5.5M5,8C5.56,8 6.08,8.15 6.53,8.42C6.38,9.85 6.8,11.27 7.66,12.38C7.16,13.34 6.16,14 5,14A3,3 0 0,1 2,11A3,3 0 0,1 5,8M19,8A3,3 0 0,1 22,11A3,3 0 0,1 19,14C17.84,14 16.84,13.34 16.34,12.38C17.2,11.27 17.62,9.85 17.47,8.42C17.92,8.15 18.44,8 19,8M5.5,18.25C5.5,16.18 8.41,14.5 12,14.5C15.59,14.5 18.5,16.18 18.5,18.25V20H5.5V18.25M0,20V18.5C0,17.11 1.89,15.94 4.45,15.6C3.86,16.28 3.5,17.22 3.5,18.25V20H0M24,20H20.5V18.25C20.5,17.22 20.14,16.28 19.55,15.6C22.11,15.94 24,17.11 24,18.5V20Z" /></svg>`
    },
    {
      key: 2,
      value: 'Gender List',
      prefixIcon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M12,17.27L18.18,21L16.54,13.97L22,9.24L14.81,8.62L12,2L9.19,8.62L2,9.24L7.46,13.97L5.82,21L12,17.27Z" /></svg>`
    },
    {
      key: 3,
      value: 'Live Events',
      prefixIcon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22C6.47,22 2,17.5 2,12A10,10 0 0,1 12,2M12.5,7V12.25L17,14.92L16.25,16.15L11,13V7H12.5Z" /></svg>`
    },
    {
      key: 4,
      value: 'SMS Events',
      prefixIcon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M20,2H4A2,2 0 0,0 2,4V22L6,18H20A2,2 0 0,0 22,16V4A2,2 0 0,0 20,2M20,16H6L4,18V4H20V16Z" /></svg>`
    }
  ]
  // Responsive, scrollable nav
  @ViewChild('navViewport') navViewport?: ElementRef<HTMLDivElement>
  canScrollLeft = false
  canScrollRight = false

  private readonly _destroyRef = inject(DestroyRef)

  constructor(private router: Router, private route: ActivatedRoute, private authService: AuthService, private ngZone: NgZone) {}

  ngOnInit(): void {
    // Check admin access
    if (!this.authService.isAdmin) {
      this.router.navigate(['/'])
      return
    }

    // Check workspace selection
    if (!this.authService.currentWorkspace) {
      this.router.navigate(['/workspaces'])
      return
    }

    // Read query params to set initial view
    this.route.queryParams.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((params) => {
      const tab = params['tab']
      const statisticTab = params['statisticTab']

      if (tab) {
        this.activeView = this._legacyEventToolTabs.has(tab) ? 'event-tools' : tab

        // If statistics tab, also handle subtab
        if (tab === 'statistics' && statisticTab !== undefined) {
          this.selectedStatisticTab = parseInt(statisticTab, 10) || 0
        }
      }
    })
  }

  ngAfterViewInit(): void {
    this.updateNavOverflow()
    // Defer to next tick to measure after view paint
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => this.updateNavOverflow(), 0)
    })
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateNavOverflow()
  }

  setActiveView(view: string): void {
    this.activeView = this._legacyEventToolTabs.has(view) ? 'event-tools' : view
  }

  goToTrustCenter(): void {
    void this.router.navigate(['/admin/trust-center/overview'])
  }

  public onStatisticSelected(option: DropdownOption): void {
    const tabIndex = typeof option.key === 'number' ? option.key : parseInt(option.key as string, 10)
    this.selectedStatisticTab = tabIndex
    this.activeView = 'statistics'
  }

  // Scrollable navbar API
  onNavViewportScroll(): void {
    this.updateNavOverflow()
  }

  onNavScroll(direction: 1 | -1): void {
    const el = this.navViewport?.nativeElement
    if (!el) return
    const amount = Math.min(280, el.clientWidth * 0.8)
    el.scrollBy({ left: amount * direction, behavior: 'smooth' })
  }

  private updateNavOverflow(): void {
    const el = this.navViewport?.nativeElement
    if (!el) return
    const maxScrollLeft = el.scrollWidth - el.clientWidth - 1 // tolerance
    this.canScrollLeft = el.scrollLeft > 1
    this.canScrollRight = el.scrollLeft < maxScrollLeft
  }
}
